<template>
  <svg
    class="m-auto block z-[1] animate-spin-slow h-full w-full"
    style="animation: spinner-rotate 3.6s linear infinite"
    width="24"
    height="24"
    viewBox="0 0 40 40"
  >
    <circle
      cx="20"
      cy="20"
      r="16"
      fill="none"
      stroke-width="4"
      :class="['stroke-gray-300 opacity-30']"
    />
    <circle
      cx="20"
      cy="20"
      r="16"
      fill="none"
      stroke-width="4"
      :class="[
        $style['spinner-ring-fg'],
        'stroke-primary stroke-linecap-round',
      ]"
      :style="{ stroke: strokeColor }"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type { UIColor } from "../../..";

const backgroundColor = inject<UIColor>("backgroundColor")!;
const copyColor = inject("copyColor", ref<UIColor>());
const iconColor = inject("iconColor", ref<UIColor>());

/**
 * Get a CSS variable value from the document root.
 */
function getCssVar(varName: string): string | null {
  const value = getComputedStyle(document.documentElement).getPropertyValue(
    varName
  );
  return value ? value.trim() : null;
}

/**
 * Convert a CSS color string (hex, rgb, hsl) to an [r,g,b] array.
 */
function parseColorToRgb(color: string): [number, number, number] | null {
  // Hex format
  if (color.startsWith("#")) {
    let hex = color.replace("#", "");
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((x) => x + x)
        .join("");
    if (hex.length === 6) {
      const num = parseInt(hex, 16);
      return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
    }
    return null;
  }
  // rgb() format
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    return [
      parseInt(rgbMatch[1]),
      parseInt(rgbMatch[2]),
      parseInt(rgbMatch[3]),
    ];
  }
  // hsl() format
  const hslMatch = color.match(/hsl\((\d+),\s*([\d.]+)%,\s*([\d.]+)%\)/);
  if (hslMatch) {
    // Convert HSL to RGB
    let h = parseInt(hslMatch[1]) / 360;
    let s = parseFloat(hslMatch[2]) / 100;
    let l = parseFloat(hslMatch[3]) / 100;
    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }
  // Not recognized
  return null;
}

/**
 * Given an [r,g,b] color, return a similar color with adjusted lightness for contrast.
 */
function getContrastingColor([r, g, b]: [number, number, number]): string {
  // Convert RGB to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  // Adjust lightness for contrast: if light, darken; if dark, lighten
  if (l > 0.5) {
    l = Math.max(0, l - 0.35);
  } else {
    l = Math.min(1, l + 0.35);
  }
  // Convert back to RGB
  function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }
  const [nr, ng, nb] = hslToRgb(h, s, l);
  return `rgb(${nr},${ng},${nb})`;
}

const strokeColor = computed(() => {
  const _getContrastingColor = () => {
    if (
      backgroundColor === "--color-white" ||
      backgroundColor === "--color-transparent"
    )
      return "var(--color-primary)";
    else if (backgroundColor === "--color-primary") return "var(--color-white)";

    const cssVar = getCssVar(backgroundColor);

    if (!cssVar) return "#fff";

    const rgb = parseColorToRgb(cssVar);

    if (!rgb) return "#fff";

    return getContrastingColor(rgb);
  };

  return copyColor.value
    ? `var(${copyColor.value})`
    : iconColor.value
    ? `var(${iconColor.value})`
    : _getContrastingColor();
});
</script>

<style module>
/*
  Custom keyframes for spinner-rotate and spinner-dash are not available in Tailwind by default.
  You need to add these to your Tailwind config:
  theme.extend.animation: {
    'spin-slow': 'spinner-rotate 3.6s linear infinite',
    'spinner-dash': 'spinner-dash 1.8s ease-in-out infinite',
  }
  and add the keyframes for spinner-rotate and spinner-dash.
*/
.spinner-ring-fg {
  stroke-linecap: round;
  stroke-dasharray: 100, 60;
  stroke-dashoffset: 60;
  animation: spinner-dash 1.8s ease-in-out infinite;
}

.push-btn-spinner-content {
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  /* Center content over spinner */
}

@keyframes spinner-rotate {
  100% {
    transform: rotate(360deg);
  }
}
@keyframes spinner-dash {
  0% {
    stroke-dasharray: 30, 110;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 50;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 30, 110;
    stroke-dashoffset: -140;
  }
}
</style>
